import {
  createBrowserRouter,
  createRoutesFromElements,
  Link,
  redirect,
  Route,
  RouterProvider,
  ScrollRestoration,
} from "react-router-dom";
import "./App.css";
import "./styles.css";
import Layout from "./pages/Layout/Layout";
import Startpage from "./pages/Startpage/Startpage";
import SearchPage from "./pages/SearchPage/SearchPage";
import TermsConditions from "./pages/TermsConditions/TermsConditions";
import Gdpr from "./pages/Gdpr/Gdpr";
import SaferAlternative from "./pages/SaferAlternative/SaferAlternative";
import SignIn from "./pages/SignIn/SignIn";
import Register from "./pages/Register/Register";
import AdPage from "./pages/AdPage/AdPage";
import {
  getAlternativeByUrl,
  getAlternativeNameByUrl,
} from "./Api/Services/Alternative";
import {
  Alternative,
  AlternativeNameResponse,
} from "./Models/AlternativeModel";
import NewAd from "./pages/NewAd/NewAd";
import { AccountContext } from "./Store/Account.store";
import { useEffect, useState } from "react";
import { AccountData } from "./Models/AccountModel";
import { getCurrentUser } from "./Api/Services/Account";
import ForgotPassword from "./pages/ForgotPassword/ForgotPassword";
import Welcome from "./pages/Welcome/Welcome";
import Profile from "./pages/Profile/Profile";
import MyAds from "./pages/MyAds/MyAds";
import NotPublished from "./pages/NotPublished/NotPublished";
import NotFound from "./pages/Error/NotFound";
import RequestAdInfo from "./pages/RequestAdInfo/RequestAdInfo";
import RequestSample from "./pages/RequestSample/RequestSample";

export default function App() {
  const [accountData, setAccountData] = useState<AccountData>(localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user") as string) : {
    isauthenticated: false,
    username: "",
    roles: [],
  });

  useEffect(() => {
    getCurrentUser().then((data) => {
      if (data.userName != "" && data.userName != undefined) {
        setAccountData({
          isauthenticated: true,
          username: data.userName,
          roles: data.roles,
        });
      }else{
        localStorage.removeItem("user");
      }
    });
  }, []);

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route
        path="/"
        element={
          <>
            <Layout />
            {/* Makes Link component send to top of screen */}
            <ScrollRestoration />
          </>
        }
        handle={{ crumb: () => <Link to="/">Start</Link> }}
      >
        <Route index element={<Startpage />} />        <Route
          path="search"
          handle={{ crumb: () => <Link to="/search">Search</Link> }}
        >
          <Route index element={<SearchPage />} />
        </Route>
        <Route
          path="alternatives"
          handle={{ crumb: () => <Link to="/alternatives">Alternatives</Link> }}
        >
          <Route index element={<SearchPage />} />
          <Route 
            path="chemical/:cas" 
            element={<SearchPage />}
            loader={async ({ params }) => {
              const { cas } = params;
              return { cas: cas || "" };
            }}
            handle={{
              crumb: (data: { cas: string }) => (
                <Link to={`/alternatives/chemical/${data.cas}`}>Chemical: {data.cas}</Link>
              ),
            }}
          />
          <Route 
            path="sinlist" 
            element={<SearchPage />}
            handle={{
              crumb: () => (
                <Link to="/alternatives/sinlist">SIN List</Link>
              ),
            }}
          />
        </Route>
        <Route
          path="my-ads"
          handle={{ crumb: () => <Link to="/my-ads">My ads</Link> }}
        >
          <Route index element={<MyAds />} />
        </Route>
        <Route
          path="404"
          handle={{ crumb: () => <Link to="/404">Not found</Link> }}
        >
          <Route index element={<NotFound />} />
        </Route>
        <Route
          path="not-published"
          handle={{
            crumb: () => <Link to="/not-published">Not published</Link>,
          }}
        >
          <Route index element={<NotPublished />} />
        </Route>
        <Route
          path="alternative/:ad"
          loader={async ({ params }) => {
            const { ad } = params;
            if (!ad) {
              return redirect("/404");
            }
            try {
              const result = await getAlternativeByUrl(ad, true);
              if (result.id === 0) {
                return redirect("/404");
              }
              return result;
            } catch (error) {
              console.error("Failed to fetch alternative:", error);
              return redirect("/404");
            }
          }}
          element={<AdPage isPreview={false} />}
          handle={{
            crumb: (data: Alternative) => {
              return (
                <Link to={`alternative${data.publicUrl}`}>{data.title}</Link>
              );
            },
          }}
        />
        <Route
          path="draft/:ad"
          loader={async ({ params }) => {
            const { ad } = params;
            if (!ad) {
              return redirect("/404");
            }
            try {
              const result = await getAlternativeByUrl(ad, false);
              if (result.id === 0) {
                return redirect("/404");
              }
              return result;
            } catch (error) {
              console.error("Failed to fetch alternative:", error);
              return redirect("/404");
            }
          }}
          element={<AdPage isPreview={false} />}
          handle={{
            crumb: (data: Alternative) => {
              return <Link to={`draft${data.publicUrl}`}>{data.title}</Link>;
            },
          }}
        />
        <Route
          path="terms-conditions"
          element={<TermsConditions />}
          handle={{
            crumb: () => (
              <Link to="terms-conditions">Terms and conditions</Link>
            ),
          }}
        />
        <Route
          path="gdpr"
          element={<Gdpr />}
          handle={{
            crumb: () => <Link to="gdpr">GDPR - Personal Data Policy</Link>,
          }}
        />
        <Route
          path="safer-alternative"
          element={<SaferAlternative />}
          handle={{
            crumb: () => (
              <Link to="safer-alternative">What is a safer alternative?</Link>
            ),
          }}
        />
        <Route
          element={<RequestAdInfo />}
          path="alternative/:ad/request-more-information"
          loader={async ({ params }) => {
            const { ad } = params;
            if (!ad) {
              return redirect("/404");
            }
            try {
              const result = await getAlternativeNameByUrl(ad);
              if (result.name === "") {
                return redirect("/404");
              }
              return result;
            } catch (error) {
              console.error("Failed to fetch alternative:", error);
              return redirect("/404");
            }
          }}
          handle={{
            crumb: (data: AlternativeNameResponse) => [
              <Link to={`/alternative${data.url}`}>{data.name}</Link>,
              <span>Request sample or quote</span>,
            ],
          }}
        />
        <Route
          path="alternative/:ad/request-sample"
          element={<RequestSample />}
          loader={async ({ params }) => {
            const { ad } = params;
            if (!ad) {
              return redirect("/404");
            }
            try {
              const result = await getAlternativeNameByUrl(ad);
              if (result.name === "") {
                return redirect("/404");
              }
              return result;
            } catch (error) {
              console.error("Failed to fetch alternative:", error);
              return redirect("/404");
            }
          }}
          handle={{
            crumb: (data: AlternativeNameResponse) => [
              <Link to={`/alternative${data.url}`}>{data.name}</Link>,
              <span>Request sample or quote</span>,
            ],
          }}
        />
        <Route path="sign-in" element={<SignIn />} />
        <Route path="register" element={<Register />} />
        <Route path="forgot-password" element={<ForgotPassword />} />
        <Route path="welcome" element={<Welcome />} />
        <Route path="profile" element={<Profile />} />
        <Route
          path="advertise-your-alternative"
          element={<NewAd />}
          handle={{
            crumb: () => (
              <Link to="advertise-your-alternative">
                Advertise your alternative
              </Link>
            ),
          }}
        />
        <Route path="*" element={<NotFound />} />
      </Route>
    )
  );

  const accountValueData = { accountData, setAccountData };

  return (
    <AccountContext.Provider value={accountValueData}>
      <RouterProvider router={router} />
    </AccountContext.Provider>
  );
}
